import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  Box,
} from "@material-ui/core";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import makeStyles from "./view-task.styles";
import { EventsIcon } from "../../../../../shared/icons";
import { Tooltips } from "../../../../../common/tooltip";
import { formattedDate } from "../../../../../shared/formattedDate";

const EditableDate = ({
  date,
  isSmsDisabled,
  smsCheck,
  emailCheck,
  onSave,
}) => {
  const classes = makeStyles();
  const [edit, setEdit] = useState(false);
  const [smsCheckState, setSmsCheckState] = useState(smsCheck);
  const [emailCheckState, setEmailCheckState] = useState(emailCheck);
  const [selectedDate, setSelectedDate] = useState(date);
  const [isDateValid, setIsDateValid] = useState(true);

  useEffect(() => {
    setSelectedDate(date);
    setSmsCheckState(smsCheck);
    setEmailCheckState(emailCheck);
  }, [date, smsCheck, emailCheck]);

  const handleCancel = () => {
    setSelectedDate(date);
    setEdit(false);
  };

  const handleSave = () => {
    onSave(selectedDate, smsCheckState, emailCheckState);
    setEdit(false);
  };

  const handleOnError = (error) => {
    if (error === "") setIsDateValid(true);
    else setIsDateValid(false);
  };

  const readOnlySegment = (
    <Typography
      className={classes.date}
      color="textPrimary"
      onClick={() => setEdit(true)}
    >
      <EventsIcon />
      {selectedDate && !edit ? (
        <>{formattedDate(selectedDate)}</>
      ) : (
        <>Set a due date</>
      )}
    </Typography>
  );

  const smsCheckSegment = (disabled = false) => (
    <FormControlLabel
      control={
        <Switch
          disabled={disabled}
          checked={smsCheckState}
          onChange={(e) => setSmsCheckState(e.target.checked)}
          color="primary"
        />
      }
      label="Text Message"
    />
  );

  const editableSegment = (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <KeyboardDatePicker
          inputVariant="outlined"
          autoOk
          disablePast
          views={["year", "month", "date"]}
          variant="inline"
          format="MM-dd-yy"
          margin="normal"
          label="Due Date"
          name="dueDate"
          id="dueDate"
          value={selectedDate}
          onChange={setSelectedDate}
          onError={handleOnError}
          KeyboardButtonProps={{
            "aria-label": "change date",
            "data-testid": "dueDate",
          }}
        />
      </MuiPickersUtilsProvider>

      {selectedDate && (
        <>
          <Typography variant="h5" className={classes.remindersTitle}>
            Reminders
          </Typography>
          <Divider className={classes.bottom} />

          {isSmsDisabled ? (
            <Tooltips title="You don't have a mobile phone associated">
              {smsCheckSegment(true)}
            </Tooltips>
          ) : (
            smsCheckSegment()
          )}
          <FormControlLabel
            control={
              <Switch
                checked={emailCheckState}
                onChange={(e) => setEmailCheckState(e.target.checked)}
                color="primary"
              />
            }
            label="Email"
          />
        </>
      )}
      <Box className={classes.buttons}>
        <Button
          variant="contained"
          className={classes.cancelButton}
          onClick={handleCancel}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          type="submit"
          className={classes.saveButton}
          color="primary"
          data-testid="btnSendData"
          onClick={handleSave}
          disabled={!isDateValid}
        >
          Update
        </Button>
      </Box>
    </>
  );

  return (
    <Box className={classes.dateEditor}>
      {edit ? editableSegment : readOnlySegment}
    </Box>
  );
};

export default EditableDate;

EditableDate.propTypes = {
  date: PropTypes.string,
  isSmsDisabled: PropTypes.bool,
  smsCheck: PropTypes.bool,
  emailCheck: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

EditableDate.defaultProps = {
  date: null,
  isSmsDisabled: false,
  smsCheck: false,
  emailCheck: false,
};
